import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRanger } from 'react-ranger';
import { cn } from '@/shared/lib/utils';

export interface SegmentRenderProps {
  segmentIndex: number;
  color: string | undefined;
  value: number;
  startValue: number;
}

interface SliderProps {
  values: number[];
  onChange?: (val: number[] | number) => void;
  maxValue: number;
  colors?: (string | undefined | null)[];
  getLabel?: (val: number) => string;
  renderSegment?: (props: SegmentRenderProps) => React.ReactNode;
}

function defaultGetLabel(val: number) {
  return val.toString();
}

export function Slider({
  maxValue,
  colors,
  getLabel = defaultGetLabel,
  renderSegment,
  values,
  onChange,
}: SliderProps) {
  const { t } = useTranslation('columbus-planner');
  const { getTrackProps, segments, handles, activeHandleIndex } = useRanger({
    min: 0,
    max: maxValue,
    stepSize: 1,
    values,
    onChange: (newValues) => onChange?.(newValues),
  });

  const [tooltip, setTooltip] = useState<{
    clientX: number;
    clientY: number;
    value: number;
    label: string;
    isExisting: boolean;
  } | null>(null);

  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const scheduleHide = () => {
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    hideTimerRef.current = setTimeout(() => setTooltip(null), 150);
  };

  const cancelHide = () => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  };

  useEffect(() => {
    if (activeHandleIndex !== null) {
      cancelHide();
      setTooltip(null);
    }
  }, [activeHandleIndex]);

  useEffect(() => {
    return () => {
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    };
  }, []);

  const trackProps = getTrackProps({});

  return (
    <div className="w-full select-none">
      {/* Track: colored bands + handles */}
      <div
        {...trackProps}
        className="h-3 w-full"
        onMouseMove={(e) => {
          if (activeHandleIndex !== null) return;
          cancelHide();
          const rect = e.currentTarget.getBoundingClientRect();
          const x = e.clientX - rect.left;
          const value = Math.max(0, Math.min(maxValue, Math.round((x / rect.width) * maxValue)));
          setTooltip({
            clientX: e.clientX,
            clientY: e.clientY,
            value,
            label: getLabel(value),
            isExisting: values.includes(value),
          });
        }}
        onMouseLeave={scheduleHide}
        onClick={(e) => {
          if (!onChange) return;
          const rect = e.currentTarget.getBoundingClientRect();
          const x = e.clientX - rect.left;
          const value = Math.max(0, Math.min(maxValue, Math.round((x / rect.width) * maxValue)));
          onChange(value);
          setTooltip(null);
        }}
      >
        {/* Colored segments */}
        {segments.map(({ getSegmentProps }, i) => {
          const { style: segStyle } = getSegmentProps({});
          return (
            <div
              key={i}
              style={{
                ...segStyle,
                height: '100%',
                backgroundColor: colors?.[i] ?? 'var(--phx-border-2)',
              }}
            />
          );
        })}

        {/* Drag handles */}
        {handles.map(({ value, active, getHandleProps }, i) => {
          const { style: handleStyle, key: _hKey, ...handleRest } = getHandleProps({});
          return (
            <button
              key={i}
              {...handleRest}
              style={handleStyle}
              type="button"
              className="appearance-none border-none bg-transparent p-0 outline-none"
              onClick={(e) => e.stopPropagation()}
            >
              <div
                className={cn(
                  'text-phx-text-inv flex cursor-col-resize items-center justify-center rounded px-2 py-0.5 text-xs font-medium whitespace-nowrap transition-all duration-300',
                  'bg-phx-surface-inv',
                  active ? '-translate-y-full scale-125' : 'scale-90'
                )}
              >
                {getLabel(value)}
              </div>
            </button>
          );
        })}
      </div>

      {/* Hover tooltip – position:fixed, neovlivňuje layout stránky */}
      {activeHandleIndex === null && tooltip && (
        <div
          className="border-phx-border bg-phx-surface pointer-events-auto fixed z-50 -translate-x-1/2 -translate-y-full rounded border whitespace-nowrap shadow-md"
          style={{ left: tooltip.clientX, top: tooltip.clientY - 6 }}
          onMouseEnter={cancelHide}
          onMouseLeave={scheduleHide}
        >
          <button
            type="button"
            className={cn(
              'flex items-center gap-1.5 rounded px-3 py-1.5 text-xs font-medium',
              tooltip.isExisting
                ? 'text-phx-danger hover:bg-phx-danger-tint'
                : 'text-phx-success-text hover:bg-phx-success-tint'
            )}
            onClick={() => {
              onChange?.(tooltip.value);
              setTooltip(null);
            }}
          >
            <span>{tooltip.isExisting ? t('slider.remove') : t('slider.add')}</span>
            <span className="text-phx-text-2 font-normal">{tooltip.label}</span>
          </button>
        </div>
      )}

      {/* Segment controls row below track */}
      {renderSegment && (
        <div className="relative mt-2 min-h-7 w-full">
          {segments.map(({ getSegmentProps, value }, i) => {
            const startValue = segments[i - 1]?.value ?? 0;
            const isEmpty =
              (i === 0 && value === 0) ||
              (i === segments.length - 1 && value === maxValue && startValue === maxValue);
            if (isEmpty) return null;
            const segProps = getSegmentProps({});
            return (
              <div key={i} style={segProps.style} className="top-0 flex justify-center px-1">
                {renderSegment({
                  segmentIndex: i,
                  color: colors?.[i] ?? undefined,
                  value,
                  startValue,
                })}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
